import React, { useState, useEffect, useCallback } from 'react';
import {
  Bell, Plus, Trash2, Power, Send, Clock, ExternalLink, Pencil,
  ChevronDown, ChevronUp, Loader2, AlertTriangle, Target, Save, X, Download,
} from 'lucide-react';
import { api } from '../api';
import { supabase } from '../supabaseClient';

interface AlertsProps {
  userId: string;
  isAdmin?: boolean;
}

interface AlertRule {
  id: string;
  methodology: string;
  timeframe: string;
  coins: string[];
  signal_buy: boolean;
  signal_strong_buy: boolean;
  signal_sell: boolean;
  signal_strong_sell: boolean;
  high_conviction_only: boolean;
  enabled: boolean;
  created_at: string;
}

interface AlertHistoryItem {
  id: string;
  symbol: string;
  methodology: string;
  timeframe: string;
  signal: string;
  confidence: number;
  price: number;
  sent_at: string;
}

interface AccuracyMethodBreakdown {
  total: number;
  wins: number;
  losses: number;
  win_rate: number;
  wl_ratio: number;
  avg_return: number;
}

interface AccuracySignalBreakdown {
  total: number;
  wins: number;
  win_rate: number;
}

interface AccuracyConvictionBreakdown {
  total: number;
  wins: number;
  losses: number;
  win_rate: number;
  wl_ratio: number;
  avg_return: number;
}

interface AccuracyData {
  total_signals: number;
  resolved: number;
  pending: number;
  wins: number;
  losses: number;
  neutral: number;
  win_rate: number;
  wl_ratio: number;
  avg_return_pct: number;
  by_methodology: Record<string, AccuracyMethodBreakdown>;
  by_signal_type: Record<string, AccuracySignalBreakdown>;
  by_conviction: Record<string, AccuracyConvictionBreakdown>;
}

interface TrackerRow {
  id: string;
  symbol: string;
  methodology: string;
  timeframe: string;
  signal: string;
  confidence: number;
  conviction: string | null;
  price_at_signal: number;
  entry_price: number;
  stop_loss: number;
  take_profit: number;
  outcome: string;
  resolved_price: number | null;
  price_change_pct: number | null;
  signal_time: string;
  resolved_at: string | null;
}

const METHODOLOGIES = [
  { value: 'technical', label: 'Technical Analysis' },
  { value: 'smc', label: 'Smart Money Concepts' },
  { value: 'priceaction', label: 'Price Action' },
  { value: 'ualgo', label: 'UAlgo Trend' },
  { value: 'elliott', label: 'Elliott Wave' },
];

const TIMEFRAMES = [
  { value: '1h', label: '1h' },
  { value: '4h', label: '4h' },
  { value: '1d', label: '1d' },
];

const METHOD_TIMEFRAMES: Record<string, string[]> = {
  technical: ['15m', '1h', '4h', '1d'],
  smc: ['1h', '4h', '1d'],
  priceaction: ['1h', '4h', '1d'],
  ualgo: ['1h', '4h', '1d'],
  elliott: ['1h', '4h', '1d'],
};

const Alerts: React.FC<AlertsProps> = ({ userId, isAdmin }) => {
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [history, setHistory] = useState<AlertHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [telegramConfigured, setTelegramConfigured] = useState(false);
  const [telegramTesting, setTelegramTesting] = useState(false);
  const [telegramMsg, setTelegramMsg] = useState('');
  const [watchlistCoins, setWatchlistCoins] = useState<string[]>([]);
  const [creatingRule, setCreatingRule] = useState(false);
  const [accuracy, setAccuracy] = useState<AccuracyData | null>(null);
  const [accuracyDays, setAccuracyDays] = useState(30);
  const [trackerRows, setTrackerRows] = useState<TrackerRow[]>([]);
  const [showTracker, setShowTracker] = useState(false);
  const [trackerLoading, setTrackerLoading] = useState(false);
  const [trackerFilter, setTrackerFilter] = useState<{ outcome?: string; methodology?: string; timeframe?: string; symbol?: string; conviction?: string }>({});

  // Edit rule state
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [editMethodology, setEditMethodology] = useState('');
  const [editTimeframe, setEditTimeframe] = useState('');
  const [editCoins, setEditCoins] = useState<string[]>([]);
  const [editBuy, setEditBuy] = useState(false);
  const [editStrongBuy, setEditStrongBuy] = useState(false);
  const [editSell, setEditSell] = useState(false);
  const [editStrongSell, setEditStrongSell] = useState(false);
  const [editHighConviction, setEditHighConviction] = useState(false);

  // New rule form state
  const [newMethodology, setNewMethodology] = useState('technical');
  const [newTimeframe, setNewTimeframe] = useState('4h');
  const [newCoins, setNewCoins] = useState<string[]>(['*']);
  const [newBuy, setNewBuy] = useState(true);
  const [newStrongBuy, setNewStrongBuy] = useState(true);
  const [newSell, setNewSell] = useState(false);
  const [newStrongSell, setNewStrongSell] = useState(false);
  const [newHighConviction, setNewHighConviction] = useState(true);

  const isDemo = userId === '0dbbfdf6-dab0-47cc-b8ed-db2170fafcb0';

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesData, historyData, configData] = await Promise.all([
        api.getAlertRules(),
        api.getAlertHistory(20),
        api.getConfig(),
      ]);
      setRules(rulesData || []);
      setHistory(historyData || []);
      setTelegramConfigured(
        !!(configData?.telegram_bot_token && configData?.telegram_chat_id)
      );
    } catch (e) {
      console.error('Failed to load alerts:', e);
    }
    setLoading(false);
  }, []);

  const loadAccuracy = useCallback(async (days: number) => {
    try {
      const data = await api.getSignalAccuracy(days);
      setAccuracy(data);
    } catch (e) {
      console.error('Failed to load accuracy:', e);
    }
  }, []);

  const loadTracker = useCallback(async (filters?: typeof trackerFilter) => {
    setTrackerLoading(true);
    try {
      const data = await api.getSignalTracker({ days: accuracyDays, ...filters });
      setTrackerRows(data);
    } catch (e) {
      console.error('Failed to load tracker:', e);
    }
    setTrackerLoading(false);
  }, [accuracyDays]);

  useEffect(() => {
    loadData();
    loadAccuracy(accuracyDays);
    // Load watchlist coins
    supabase
      .from('watchlists')
      .select('coin_symbol')
      .eq('user_id', userId)
      .order('position', { ascending: true })
      .then(({ data }) => {
        if (data && data.length > 0) {
          setWatchlistCoins(data.map(d => d.coin_symbol));
        } else {
          setWatchlistCoins(['BTC', 'ETH', 'SOL', 'XRP', 'SUI', 'LINK', 'ADA', 'NEAR', 'DOGE']);
        }
      });
  }, [userId, loadData]);

  const handleCreateRule = async () => {
    setSaving(true);
    try {
      await api.createAlertRule({
        methodology: newMethodology,
        timeframe: newTimeframe,
        coins: newCoins,
        signal_buy: newBuy,
        signal_strong_buy: newStrongBuy,
        signal_sell: newSell,
        signal_strong_sell: newStrongSell,
        high_conviction_only: newMethodology === 'technical' ? false : newHighConviction,
      });
      await loadData();
      setCreatingRule(false);
      // Reset form
      setNewMethodology('technical');
      setNewTimeframe('4h');
      setNewCoins(['*']);
      setNewBuy(true);
      setNewStrongBuy(true);
      setNewSell(false);
      setNewStrongSell(false);
      setNewHighConviction(false);
    } catch (e: any) {
      alert(e.message || 'Failed to create rule');
    }
    setSaving(false);
  };

  const handleToggleRule = async (rule: AlertRule) => {
    try {
      await api.updateAlertRule(rule.id, { enabled: !rule.enabled });
      setRules(prev =>
        prev.map(r => (r.id === rule.id ? { ...r, enabled: !r.enabled } : r))
      );
    } catch (e: any) {
      alert(e.message || 'Failed to update rule');
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!window.confirm('Delete this alert rule?')) return;
    try {
      await api.deleteAlertRule(ruleId);
      setRules(prev => prev.filter(r => r.id !== ruleId));
    } catch (e: any) {
      alert(e.message || 'Failed to delete rule');
    }
  };

  const startEditing = (rule: AlertRule) => {
    setEditingRuleId(rule.id);
    setEditMethodology(rule.methodology);
    setEditTimeframe(rule.timeframe);
    setEditCoins([...rule.coins]);
    setEditBuy(rule.signal_buy);
    setEditStrongBuy(rule.signal_strong_buy);
    setEditSell(rule.signal_sell);
    setEditStrongSell(rule.signal_strong_sell);
    setEditHighConviction(rule.high_conviction_only || false);
  };

  const cancelEditing = () => {
    setEditingRuleId(null);
  };

  const handleSaveEdit = async () => {
    if (!editingRuleId) return;
    setSaving(true);
    try {
      await api.updateAlertRule(editingRuleId, {
        methodology: editMethodology,
        timeframe: editTimeframe,
        coins: editCoins,
        signal_buy: editBuy,
        signal_strong_buy: editStrongBuy,
        signal_sell: editSell,
        signal_strong_sell: editStrongSell,
        high_conviction_only: editMethodology === 'technical' ? false : editHighConviction,
      });
      await loadData();
      setEditingRuleId(null);
    } catch (e: any) {
      alert(e.message || 'Failed to update rule');
    }
    setSaving(false);
  };

  const toggleEditCoin = (coin: string) => {
    setEditCoins(prev => {
      if (prev.includes('*')) {
        return [coin];
      }
      if (prev.includes(coin)) {
        const next = prev.filter(c => c !== coin);
        return next.length === 0 ? ['*'] : next;
      }
      return [...prev, coin];
    });
  };

  const handleTestTelegram = async () => {
    setTelegramTesting(true);
    setTelegramMsg('');
    try {
      await api.testTelegram();
      setTelegramMsg('Test sent!');
    } catch (e: any) {
      setTelegramMsg(e.message || 'Test failed');
    }
    setTelegramTesting(false);
  };

  const getMethodLabel = (m: string) =>
    METHODOLOGIES.find(x => x.value === m)?.label || m;

  const getSignalColor = (signal: string) => {
    if (signal.includes('BUY')) return 'text-green-500';
    if (signal.includes('SELL')) return 'text-red-500';
    return 'text-gray-400';
  };

  const getSignalBg = (signal: string) => {
    if (signal.includes('BUY')) return 'bg-green-500/10 border-green-500/20';
    if (signal.includes('SELL')) return 'bg-red-500/10 border-red-500/20';
    return 'bg-gray-500/10 border-gray-500/20';
  };

  const formatPrice = (price: number): string => {
    if (price === 0) return '0.00';
    if (price >= 1) return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const decimals = Math.max(2, -Math.floor(Math.log10(price)) + 2);
    return price.toFixed(Math.min(decimals, 8));
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const lastFiredForRule = (ruleId: string) => {
    const match = history.find(h => {
      // Match by methodology since rule_id might not always match after recreation
      return true; // We'll filter by rule in a moment
    });
    // Find history entries matching this rule
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) return null;
    const ruleHistory = history.find(
      h => h.methodology === rule.methodology && h.timeframe === rule.timeframe
    );
    return ruleHistory;
  };

  const toggleCoin = (coin: string) => {
    setNewCoins(prev => {
      if (prev.includes('*')) {
        // Switching from "all" to specific
        return [coin];
      }
      if (prev.includes(coin)) {
        const next = prev.filter(c => c !== coin);
        return next.length === 0 ? ['*'] : next;
      }
      return [...prev, coin];
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-7 h-7 text-blue-500 animate-spin mr-3" />
        <span className="text-gray-400">Loading alerts...</span>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Bell className="w-5 h-5 text-blue-400" />
            Signal Alerts
          </h1>
          <p style={{ fontSize: 13, color: '#71717a', marginTop: 4 }}>
            Push watchlist signals to Telegram automatically
          </p>
        </div>
        {!isDemo && (
          <button
            onClick={() => setCreatingRule(true)}
            disabled={creatingRule || rules.length >= 10}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            New Rule
          </button>
        )}
      </div>

      {/* Demo banner */}
      {isDemo && (
        <div className="flex items-center gap-2 mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-sm text-yellow-400">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          Demo account — alert rules are read-only. Sign up to configure your own alerts.
        </div>
      )}

      {/* Telegram Status */}
      <div className={`flex items-center justify-between p-3 mb-5 rounded-lg border ${
        telegramConfigured
          ? 'bg-green-500/5 border-green-500/20'
          : 'bg-yellow-500/5 border-yellow-500/20'
      }`}>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${telegramConfigured ? 'bg-green-500' : 'bg-yellow-500'}`} />
          <span className={`text-sm ${telegramConfigured ? 'text-green-400' : 'text-yellow-400'}`}>
            {telegramConfigured ? 'Telegram Connected' : 'Telegram Not Configured'}
          </span>
          {!telegramConfigured && (
            <a href="/settings" className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 ml-2">
              Configure in Settings <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
        {telegramConfigured && (
          <div className="flex items-center gap-2">
            {telegramMsg && (
              <span className={`text-xs ${telegramMsg.includes('sent') ? 'text-green-400' : 'text-red-400'}`}>
                {telegramMsg}
              </span>
            )}
            <button
              onClick={handleTestTelegram}
              disabled={telegramTesting}
              className="flex items-center gap-1 px-2.5 py-1 bg-gray-800 border border-gray-700 hover:border-gray-600 rounded text-xs text-gray-300 hover:text-white transition-colors disabled:opacity-50"
            >
              <Send className="w-3 h-3" />
              {telegramTesting ? 'Sending...' : 'Send Test'}
            </button>
          </div>
        )}
      </div>

      {/* Create Rule Form */}
      {creatingRule && (
        <div className="bg-gray-900 border border-blue-500/30 rounded-lg p-5 mb-5">
          <h3 className="text-sm font-semibold text-blue-400 mb-4">New Alert Rule</h3>

          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* Methodology */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Methodology</label>
              <select
                value={newMethodology}
                onChange={e => {
                  setNewMethodology(e.target.value);
                  // Reset timeframe if not available for new methodology
                  const available = METHOD_TIMEFRAMES[e.target.value] || ['4h'];
                  if (!available.includes(newTimeframe)) {
                    setNewTimeframe(available.includes('4h') ? '4h' : available[0]);
                  }
                }}
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
              >
                {METHODOLOGIES.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            {/* Timeframe */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Timeframe</label>
              <select
                value={newTimeframe}
                onChange={e => setNewTimeframe(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
              >
                {(METHOD_TIMEFRAMES[newMethodology] || ['4h']).map(tf => (
                  <option key={tf} value={tf}>{tf}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Coins */}
          <div className="mb-4">
            <label className="block text-xs text-gray-400 mb-1.5">Coins</label>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setNewCoins(['*'])}
                className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                  newCoins.includes('*')
                    ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                    : 'bg-gray-800 text-gray-400 border-gray-700 hover:text-white'
                }`}
              >
                All Watchlist
              </button>
              {watchlistCoins.map(coin => (
                <button
                  key={coin}
                  onClick={() => toggleCoin(coin)}
                  className={`px-2.5 py-1 rounded text-xs font-mono border transition-colors ${
                    !newCoins.includes('*') && newCoins.includes(coin)
                      ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                      : 'bg-gray-800 text-gray-400 border-gray-700 hover:text-white'
                  }`}
                >
                  {coin}
                </button>
              ))}
            </div>
          </div>

          {/* Signal Types */}
          <div className="mb-4">
            <label className="block text-xs text-gray-400 mb-1.5">Signal Types</label>
            <div className="flex flex-wrap gap-3">
              {[
                { key: 'buy', label: 'BUY', state: newBuy, setter: setNewBuy, color: 'green' },
                { key: 'strongBuy', label: 'STRONG BUY', state: newStrongBuy, setter: setNewStrongBuy, color: 'green' },
                { key: 'sell', label: 'SELL', state: newSell, setter: setNewSell, color: 'red' },
                { key: 'strongSell', label: 'STRONG SELL', state: newStrongSell, setter: setNewStrongSell, color: 'red' },
              ].map(s => (
                <label key={s.key} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={s.state}
                    onChange={() => s.setter(!s.state)}
                    className="rounded"
                  />
                  <span className={`text-xs font-medium ${
                    s.color === 'green' ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {s.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Conviction Filter */}
          <div className="mb-5">
            <label className="block text-xs text-gray-400 mb-2">Conviction Filter</label>
            {newMethodology === 'technical' ? (
              <div className="text-[11px] text-gray-500 italic">
                Not available for Technical Analysis (score-based, no trade setups)
              </div>
            ) : (
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="newConviction"
                    checked={!newHighConviction}
                    onChange={() => setNewHighConviction(false)}
                    className="text-blue-500"
                  />
                  <span className="text-xs text-white">High and Low</span>
                  <span className="text-[11px] text-gray-500">&mdash; Alert on all signals</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="newConviction"
                    checked={newHighConviction}
                    onChange={() => setNewHighConviction(true)}
                    className="text-blue-500"
                  />
                  <span className="text-xs text-white">High only</span>
                  <span className="text-[11px] text-gray-500">&mdash; R:R &ge; 2.0 + quality checks pass</span>
                </label>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleCreateRule}
              disabled={saving || (!newBuy && !newStrongBuy && !newSell && !newStrongSell)}
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded text-sm font-medium transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {saving ? 'Creating...' : 'Create Rule'}
            </button>
            <button
              onClick={() => setCreatingRule(false)}
              className="px-4 py-2 bg-gray-800 border border-gray-700 hover:border-gray-600 text-gray-300 rounded text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Rules */}
      {rules.length === 0 && !creatingRule ? (
        <div className="text-center py-16 text-gray-500">
          <Bell className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm mb-1">No alert rules configured</p>
          <p className="text-xs text-gray-600">
            Create a rule to start receiving signal alerts on Telegram
          </p>
        </div>
      ) : (
        <div className="space-y-3 mb-6">
          {rules.map(rule => {
            const lastFired = lastFiredForRule(rule.id);
            return (
              <div
                key={rule.id}
                className={`bg-gray-900 border rounded-lg p-4 transition-colors ${
                  editingRuleId === rule.id ? 'border-blue-500/30' :
                  rule.enabled ? 'border-gray-800 hover:border-gray-700' : 'border-gray-800/50 opacity-60'
                }`}
              >
                {editingRuleId === rule.id ? (
                  /* Inline Edit Form */
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-blue-400">Edit Alert Rule</h3>
                      <button onClick={cancelEditing} className="p-1 text-gray-500 hover:text-white transition-colors">
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Warning */}
                    <div className="flex items-start gap-2 mb-4 p-2 bg-yellow-500/5 border border-yellow-500/10 rounded text-[11px] text-yellow-500/80">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      Changes apply to future alerts only. Past alert history remains linked to this rule.
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5">Methodology</label>
                        <select
                          value={editMethodology}
                          onChange={e => {
                            setEditMethodology(e.target.value);
                            const available = METHOD_TIMEFRAMES[e.target.value] || ['4h'];
                            if (!available.includes(editTimeframe)) {
                              setEditTimeframe(available.includes('4h') ? '4h' : available[0]);
                            }
                          }}
                          className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
                        >
                          {METHODOLOGIES.map(m => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5">Timeframe</label>
                        <select
                          value={editTimeframe}
                          onChange={e => setEditTimeframe(e.target.value)}
                          className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
                        >
                          {(METHOD_TIMEFRAMES[editMethodology] || ['4h']).map(tf => (
                            <option key={tf} value={tf}>{tf}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Coins */}
                    <div className="mb-4">
                      <label className="block text-xs text-gray-400 mb-1.5">Coins</label>
                      <div className="flex flex-wrap gap-1.5">
                        <button
                          onClick={() => setEditCoins(['*'])}
                          className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                            editCoins.includes('*')
                              ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                              : 'bg-gray-800 text-gray-400 border-gray-700 hover:text-white'
                          }`}
                        >
                          All Watchlist
                        </button>
                        {watchlistCoins.map(coin => (
                          <button
                            key={coin}
                            onClick={() => toggleEditCoin(coin)}
                            className={`px-2.5 py-1 rounded text-xs font-mono border transition-colors ${
                              !editCoins.includes('*') && editCoins.includes(coin)
                                ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                                : 'bg-gray-800 text-gray-400 border-gray-700 hover:text-white'
                            }`}
                          >
                            {coin}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Signal Types */}
                    <div className="mb-4">
                      <label className="block text-xs text-gray-400 mb-1.5">Signal Types</label>
                      <div className="flex flex-wrap gap-3">
                        {[
                          { key: 'buy', label: 'BUY', state: editBuy, setter: setEditBuy, color: 'green' },
                          { key: 'strongBuy', label: 'STRONG BUY', state: editStrongBuy, setter: setEditStrongBuy, color: 'green' },
                          { key: 'sell', label: 'SELL', state: editSell, setter: setEditSell, color: 'red' },
                          { key: 'strongSell', label: 'STRONG SELL', state: editStrongSell, setter: setEditStrongSell, color: 'red' },
                        ].map(s => (
                          <label key={s.key} className="flex items-center gap-1.5 cursor-pointer">
                            <input type="checkbox" checked={s.state} onChange={() => s.setter(!s.state)} className="rounded" />
                            <span className={`text-xs font-medium ${s.color === 'green' ? 'text-green-400' : 'text-red-400'}`}>
                              {s.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Conviction Filter */}
                    <div className="mb-4">
                      <label className="block text-xs text-gray-400 mb-2">Conviction Filter</label>
                      {editMethodology === 'technical' ? (
                        <div className="text-[11px] text-gray-500 italic">
                          Not available for Technical Analysis (score-based, no trade setups)
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="editConviction"
                              checked={!editHighConviction}
                              onChange={() => setEditHighConviction(false)}
                              className="text-blue-500"
                            />
                            <span className="text-xs text-white">High and Low</span>
                            <span className="text-[11px] text-gray-500">&mdash; Alert on all signals</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="editConviction"
                              checked={editHighConviction}
                              onChange={() => setEditHighConviction(true)}
                              className="text-blue-500"
                            />
                            <span className="text-xs text-white">High only</span>
                            <span className="text-[11px] text-gray-500">&mdash; R:R &ge; 2.0 + quality checks pass</span>
                          </label>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={saving || (!editBuy && !editStrongBuy && !editSell && !editStrongSell)}
                        className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded text-sm font-medium transition-colors disabled:opacity-50"
                      >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        {saving ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="px-4 py-2 bg-gray-800 border border-gray-700 hover:border-gray-600 text-gray-300 rounded text-sm transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Normal Rule Display */
                  <>
                    {/* Rule header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-white">
                          {getMethodLabel(rule.methodology)}
                        </span>
                        <span className="px-1.5 py-0.5 bg-gray-800 rounded text-xs font-mono text-gray-400">
                          {rule.timeframe}
                        </span>
                        {rule.high_conviction_only && (
                          <span className="px-1.5 py-0.5 bg-green-500/10 text-green-400 rounded text-[10px] border border-green-500/20 font-medium">
                            HIGH ONLY
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!isDemo && (
                          <>
                            <button
                              onClick={() => startEditing(rule)}
                              className="p-1 text-gray-600 hover:text-blue-400 transition-colors"
                              title="Edit rule"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleToggleRule(rule)}
                              className={`flex items-center gap-1 px-2 py-1 rounded text-xs border transition-colors ${
                                rule.enabled
                                  ? 'bg-green-500/10 text-green-400 border-green-500/20 hover:bg-green-500/20'
                                  : 'bg-gray-800 text-gray-500 border-gray-700 hover:text-white'
                              }`}
                            >
                              <Power className="w-3 h-3" />
                              {rule.enabled ? 'ON' : 'OFF'}
                            </button>
                            <button
                              onClick={() => handleDeleteRule(rule.id)}
                              className="p-1 text-gray-600 hover:text-red-400 transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Coins */}
                    <div className="flex flex-wrap items-center gap-1 mb-2">
                      <span className="text-xs text-gray-500 mr-1">Coins:</span>
                      {rule.coins.includes('*') ? (
                        <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 rounded text-xs border border-blue-500/20">
                          All Watchlist
                        </span>
                      ) : (
                        rule.coins.map(c => (
                          <span key={c} className="px-1.5 py-0.5 bg-gray-800 text-gray-300 rounded text-xs font-mono">
                            {c}
                          </span>
                        ))
                      )}
                    </div>

                    {/* Signal types */}
                    <div className="flex flex-wrap items-center gap-1.5 mb-2">
                      <span className="text-xs text-gray-500 mr-1">Signals:</span>
                      {rule.signal_buy && (
                        <span className="px-1.5 py-0.5 bg-green-500/10 text-green-400 rounded text-[11px] border border-green-500/20">
                          BUY
                        </span>
                      )}
                      {rule.signal_strong_buy && (
                        <span className="px-1.5 py-0.5 bg-green-500/15 text-green-400 rounded text-[11px] border border-green-500/30 font-semibold">
                          STRONG BUY
                        </span>
                      )}
                      {rule.signal_sell && (
                        <span className="px-1.5 py-0.5 bg-red-500/10 text-red-400 rounded text-[11px] border border-red-500/20">
                          SELL
                        </span>
                      )}
                      {rule.signal_strong_sell && (
                        <span className="px-1.5 py-0.5 bg-red-500/15 text-red-400 rounded text-[11px] border border-red-500/30 font-semibold">
                          STRONG SELL
                        </span>
                      )}
                    </div>

                    {/* Last fired */}
                    {lastFired && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        Last: {lastFired.symbol} {lastFired.signal.replace('_', ' ')} {lastFired.confidence}% — {timeAgo(lastFired.sent_at)}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Alert History */}
      {history.length > 0 && (
        <div className="mt-6">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-3"
          >
            {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Recent Alerts ({history.length})
          </button>

          {showHistory && (
            <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-gray-800">
                    <th className="px-3 py-2 text-left">Time</th>
                    <th className="px-3 py-2 text-left">Coin</th>
                    <th className="px-3 py-2 text-left">Method</th>
                    <th className="px-3 py-2 text-left">Signal</th>
                    <th className="px-3 py-2 text-right">Conf</th>
                    <th className="px-3 py-2 text-right">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id} className="text-xs border-b border-gray-800/50 hover:bg-gray-800/30">
                      <td className="px-3 py-2 text-gray-500 font-mono">
                        {timeAgo(h.sent_at)}
                      </td>
                      <td className="px-3 py-2 font-mono font-medium text-white">
                        {h.symbol}
                      </td>
                      <td className="px-3 py-2 text-gray-400">
                        {getMethodLabel(h.methodology)} {h.timeframe}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-1.5 py-0.5 rounded border text-[11px] font-medium ${getSignalBg(h.signal)} ${getSignalColor(h.signal)}`}>
                          {h.signal.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-gray-400 font-mono">
                        {h.confidence}%
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-gray-300">
                        ${formatPrice(h.price)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Signal Accuracy (admin only) */}
      {isAdmin && <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-white">
            <Target className="w-4 h-4 text-blue-400" />
            Signal Accuracy
          </h2>
          <div className="flex gap-1">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => { setAccuracyDays(d); loadAccuracy(d); }}
                className={`px-2 py-1 rounded text-xs font-mono ${
                  accuracyDays === d
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {accuracy && accuracy.total_signals > 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-5">
            {/* Overall stats */}
            <div className="grid grid-cols-5 gap-4 mb-5">
              <div className="text-center">
                <div className={`text-3xl font-bold font-mono ${
                  accuracy.win_rate >= 60 ? 'text-green-400' : accuracy.win_rate >= 50 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {accuracy.win_rate}%
                </div>
                <div className="text-[11px] text-gray-500 mt-1">Win Rate</div>
              </div>
              <div className="text-center">
                <div className={`text-xl font-bold font-mono ${
                  accuracy.wl_ratio >= 60 ? 'text-green-400' : accuracy.wl_ratio >= 50 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {accuracy.wl_ratio}%
                </div>
                <div className="text-[11px] text-gray-500 mt-1">W/L Ratio</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold font-mono text-white">{accuracy.resolved}</div>
                <div className="text-[11px] text-gray-500 mt-1">Resolved</div>
              </div>
              <div className="text-center">
                <div className={`text-xl font-bold font-mono ${accuracy.avg_return_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {accuracy.avg_return_pct >= 0 ? '+' : ''}{accuracy.avg_return_pct}%
                </div>
                <div className="text-[11px] text-gray-500 mt-1">Avg Return</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold font-mono text-gray-400">{accuracy.pending}</div>
                <div className="text-[11px] text-gray-500 mt-1">Pending</div>
              </div>
            </div>

            {/* W/L/N bar */}
            {accuracy.resolved > 0 && (
              <div className="flex rounded-full overflow-hidden h-2 mb-5 bg-gray-700">
                {accuracy.wins > 0 && (
                  <div className="bg-green-500" style={{ width: `${(accuracy.wins / accuracy.resolved) * 100}%` }} />
                )}
                {accuracy.neutral > 0 && (
                  <div className="bg-gray-500" style={{ width: `${(accuracy.neutral / accuracy.resolved) * 100}%` }} />
                )}
                {accuracy.losses > 0 && (
                  <div className="bg-red-500" style={{ width: `${(accuracy.losses / accuracy.resolved) * 100}%` }} />
                )}
              </div>
            )}

            {/* By Methodology */}
            {Object.keys(accuracy.by_methodology).length > 0 && (
              <div className="mb-4">
                <div className="text-xs text-gray-400 uppercase mb-2">By Methodology</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {Object.entries(accuracy.by_methodology).map(([method, data]) => (
                    <div key={method} className="bg-gray-800/50 rounded p-2 border border-gray-700">
                      <div className="text-[11px] text-gray-400 capitalize mb-1">
                        {getMethodLabel(method)}
                      </div>
                      <div className={`text-lg font-bold font-mono ${
                        data.win_rate >= 60 ? 'text-green-400' : data.win_rate >= 50 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {data.win_rate}%
                      </div>
                      <div className="text-[10px] text-gray-500">
                        {data.total} signals &middot; {data.wins}W/{data.losses}L &middot; {data.avg_return >= 0 ? '+' : ''}{data.avg_return}% avg
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* By Conviction Tier */}
            {accuracy.by_conviction && Object.keys(accuracy.by_conviction).length > 0 && (
              <div className="mb-4">
                <div className="text-xs text-gray-400 uppercase mb-2">By Conviction</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.entries(accuracy.by_conviction)
                    .sort(([a], [b]) => (a === 'HIGH' ? -1 : b === 'HIGH' ? 1 : 0))
                    .map(([tier, data]) => (
                    <div key={tier} className={`rounded p-2 border ${
                      tier === 'HIGH' ? 'bg-green-500/5 border-green-500/20' :
                      tier === 'LOW' ? 'bg-yellow-500/5 border-yellow-500/20' :
                      'bg-gray-800/50 border-gray-700'
                    }`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className={`text-[11px] font-bold ${
                          tier === 'HIGH' ? 'text-green-400' :
                          tier === 'LOW' ? 'text-yellow-500' :
                          'text-gray-400'
                        }`}>
                          {tier === 'NONE' ? 'N/A' : tier}
                        </span>
                      </div>
                      <div className={`text-lg font-bold font-mono ${
                        data.win_rate >= 60 ? 'text-green-400' : data.win_rate >= 50 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {data.win_rate}%
                      </div>
                      <div className="text-[10px] text-gray-500">
                        {data.total} signals &middot; {data.wins}W/{data.losses}L &middot; {data.avg_return >= 0 ? '+' : ''}{data.avg_return}% avg
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* By Signal Type */}
            {Object.keys(accuracy.by_signal_type).length > 0 && (
              <div className="mb-3">
                <div className="text-xs text-gray-400 uppercase mb-2">By Signal Type</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(accuracy.by_signal_type).map(([sig, data]) => (
                    <div key={sig} className={`flex items-center gap-2 px-3 py-1.5 rounded border ${getSignalBg(sig)}`}>
                      <span className={`text-xs font-medium ${getSignalColor(sig)}`}>
                        {sig.replace('_', ' ')}
                      </span>
                      <span className="text-xs font-mono text-gray-300">
                        {data.win_rate}% ({data.wins}/{data.total})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="text-[11px] text-gray-600">
              Based on {accuracy.resolved} resolved signal{accuracy.resolved !== 1 ? 's' : ''}.
              Outcome checked after signal timeframe period. Win = price moved &ge;0.5% in predicted direction.
            </div>
          </div>
        ) : (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 text-center">
            <Target className="w-8 h-8 mx-auto mb-2 text-gray-700" />
            <p className="text-sm text-gray-500">No signal data yet</p>
            <p className="text-xs text-gray-600 mt-1">
              Signals are tracked automatically every 5 minutes. Check back after signals fire and resolve.
            </p>
          </div>
        )}
      </div>}

      {/* Signal Log — Detailed Table (admin only) */}
      {isAdmin && <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-white">
            <Clock className="w-4 h-4 text-blue-400" />
            Signal Log
          </h2>
          <div className="flex gap-2">
            {showTracker && trackerRows.length > 0 && (
              <button
                onClick={() => {
                  const headers = ['Time','Symbol','Signal','Methodology','Timeframe','Confidence','Conviction','Signal Price','Entry','Stop Loss','Take Profit','R:R','Outcome','Resolved Price','Move %','Resolved At'];
                  const csvRows = trackerRows.map(r => {
                    const rr = r.stop_loss && r.take_profit && r.entry_price
                      ? Math.abs((r.take_profit - r.entry_price) / (r.entry_price - r.stop_loss)).toFixed(2)
                      : '';
                    return [
                      r.signal_time, r.symbol, r.signal, r.methodology, r.timeframe,
                      r.confidence ?? '', r.conviction ?? '', r.price_at_signal ?? '',
                      r.entry_price ?? '', r.stop_loss ?? '', r.take_profit ?? '', rr,
                      r.outcome, r.resolved_price ?? '', r.price_change_pct ?? '', r.resolved_at ?? '',
                    ].map(v => `"${v}"`).join(',');
                  });
                  const csv = [headers.join(','), ...csvRows].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `signal_log_${new Date().toISOString().slice(0, 10)}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-gray-800 text-gray-400 border border-gray-700 hover:text-white"
              >
                <Download className="w-3 h-3" />
                CSV
              </button>
            )}
            <button
              onClick={() => {
                if (!showTracker) loadTracker(trackerFilter);
                setShowTracker(!showTracker);
              }}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-gray-800 text-gray-400 border border-gray-700 hover:text-white"
            >
              {showTracker ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {showTracker ? 'Hide' : 'Show'} Details
            </button>
          </div>
        </div>

        {showTracker && (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-3">
              <select
                value={trackerFilter.outcome || ''}
                onChange={e => { const f = { ...trackerFilter, outcome: e.target.value || undefined }; setTrackerFilter(f); loadTracker(f); }}
                className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white"
              >
                <option value="">All Outcomes</option>
                <option value="WIN">WIN</option>
                <option value="LOSS">LOSS</option>
                <option value="NEUTRAL">NEUTRAL</option>
                <option value="PENDING">PENDING</option>
              </select>
              <select
                value={trackerFilter.conviction || ''}
                onChange={e => { const f = { ...trackerFilter, conviction: e.target.value || undefined }; setTrackerFilter(f); loadTracker(f); }}
                className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white"
              >
                <option value="">All Conviction</option>
                <option value="HIGH">HIGH</option>
                <option value="LOW">LOW</option>
              </select>
              <select
                value={trackerFilter.methodology || ''}
                onChange={e => { const f = { ...trackerFilter, methodology: e.target.value || undefined }; setTrackerFilter(f); loadTracker(f); }}
                className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white"
              >
                <option value="">All Methods</option>
                {METHODOLOGIES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <select
                value={trackerFilter.timeframe || ''}
                onChange={e => { const f = { ...trackerFilter, timeframe: e.target.value || undefined }; setTrackerFilter(f); loadTracker(f); }}
                className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white"
              >
                <option value="">All TFs</option>
                {TIMEFRAMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <input
                type="text"
                placeholder="Symbol..."
                value={trackerFilter.symbol || ''}
                onChange={e => {
                  const val = e.target.value.toUpperCase().replace(/[^A-Z]/g, '');
                  setTrackerFilter(f => ({ ...f, symbol: val || undefined }));
                }}
                onBlur={() => loadTracker(trackerFilter)}
                onKeyDown={e => { if (e.key === 'Enter') loadTracker(trackerFilter); }}
                className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white w-20"
              />
              <span className="text-[11px] text-gray-500 self-center ml-auto">
                {trackerRows.length} signal{trackerRows.length !== 1 ? 's' : ''}
              </span>
            </div>

            {trackerLoading ? (
              <div className="text-center py-8 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : trackerRows.length === 0 ? (
              <div className="text-center py-6 text-gray-500 text-xs">No signals found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-400 border-b border-gray-800">
                      <th className="text-left py-2 px-1 font-medium">Time</th>
                      <th className="text-left py-2 px-1 font-medium">Symbol</th>
                      <th className="text-left py-2 px-1 font-medium">Signal</th>
                      <th className="text-left py-2 px-1 font-medium">Method</th>
                      <th className="text-left py-2 px-1 font-medium">TF</th>
                      <th className="text-right py-2 px-1 font-medium">Conf</th>
                      <th className="text-left py-2 px-1 font-medium">Conv</th>
                      <th className="text-right py-2 px-1 font-medium">Signal $</th>
                      <th className="text-right py-2 px-1 font-medium">Entry</th>
                      <th className="text-right py-2 px-1 font-medium">SL</th>
                      <th className="text-right py-2 px-1 font-medium">TP</th>
                      <th className="text-right py-2 px-1 font-medium">R:R</th>
                      <th className="text-left py-2 px-1 font-medium">Outcome</th>
                      <th className="text-right py-2 px-1 font-medium">Resolve $</th>
                      <th className="text-right py-2 px-1 font-medium">Move %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trackerRows.map(row => {
                      const rr = row.stop_loss && row.take_profit && row.entry_price
                        ? Math.abs((row.take_profit - row.entry_price) / (row.entry_price - row.stop_loss))
                        : null;
                      const isBuy = row.signal.includes('BUY');
                      return (
                        <tr key={row.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                          <td className="py-1.5 px-1 text-gray-400 whitespace-nowrap font-mono">
                            {new Date(row.signal_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}{' '}
                            {new Date(row.signal_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                          </td>
                          <td className="py-1.5 px-1 text-white font-medium">{row.symbol}</td>
                          <td className="py-1.5 px-1">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              row.signal === 'STRONG_BUY' ? 'bg-green-500/20 text-green-400' :
                              row.signal === 'BUY' ? 'bg-green-500/10 text-green-300' :
                              row.signal === 'STRONG_SELL' ? 'bg-red-500/20 text-red-400' :
                              'bg-red-500/10 text-red-300'
                            }`}>
                              {row.signal.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="py-1.5 px-1 text-gray-400 capitalize">{row.methodology}</td>
                          <td className="py-1.5 px-1 text-gray-400">{row.timeframe}</td>
                          <td className="py-1.5 px-1 text-right font-mono text-gray-300">{row.confidence}</td>
                          <td className="py-1.5 px-1">
                            {row.conviction ? (
                              <span className={`text-[10px] font-bold ${
                                row.conviction === 'HIGH' ? 'text-green-400' : 'text-yellow-500'
                              }`}>{row.conviction}</span>
                            ) : <span className="text-gray-600">—</span>}
                          </td>
                          <td className="py-1.5 px-1 text-right font-mono text-gray-300">
                            {row.price_at_signal != null ? (row.price_at_signal < 1 ? row.price_at_signal.toFixed(4) : row.price_at_signal < 100 ? row.price_at_signal.toFixed(2) : row.price_at_signal.toLocaleString(undefined, { maximumFractionDigits: 0 })) : '—'}
                          </td>
                          <td className="py-1.5 px-1 text-right font-mono text-gray-300">
                            {row.entry_price != null ? (row.entry_price < 1 ? row.entry_price.toFixed(4) : row.entry_price < 100 ? row.entry_price.toFixed(2) : row.entry_price.toLocaleString(undefined, { maximumFractionDigits: 0 })) : '—'}
                          </td>
                          <td className="py-1.5 px-1 text-right font-mono text-red-400/70">
                            {row.stop_loss != null ? (row.stop_loss < 1 ? row.stop_loss.toFixed(4) : row.stop_loss < 100 ? row.stop_loss.toFixed(2) : row.stop_loss.toLocaleString(undefined, { maximumFractionDigits: 0 })) : '—'}
                          </td>
                          <td className="py-1.5 px-1 text-right font-mono text-green-400/70">
                            {row.take_profit != null ? (row.take_profit < 1 ? row.take_profit.toFixed(4) : row.take_profit < 100 ? row.take_profit.toFixed(2) : row.take_profit.toLocaleString(undefined, { maximumFractionDigits: 0 })) : '—'}
                          </td>
                          <td className="py-1.5 px-1 text-right font-mono text-gray-300">
                            {rr !== null ? rr.toFixed(1) : '—'}
                          </td>
                          <td className="py-1.5 px-1">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              row.outcome === 'WIN' ? 'bg-green-500/20 text-green-400' :
                              row.outcome === 'LOSS' ? 'bg-red-500/20 text-red-400' :
                              row.outcome === 'NEUTRAL' ? 'bg-gray-600/20 text-gray-400' :
                              'bg-blue-500/10 text-blue-400'
                            }`}>
                              {row.outcome}
                            </span>
                          </td>
                          <td className="py-1.5 px-1 text-right font-mono text-gray-400">
                            {row.resolved_price != null ? (
                              row.resolved_price < 1 ? row.resolved_price.toFixed(4) : row.resolved_price < 100 ? row.resolved_price.toFixed(2) : row.resolved_price.toLocaleString(undefined, { maximumFractionDigits: 0 })
                            ) : '—'}
                          </td>
                          <td className={`py-1.5 px-1 text-right font-mono ${
                            row.price_change_pct == null ? 'text-gray-600' :
                            (isBuy ? row.price_change_pct >= 0 : row.price_change_pct <= 0) ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {row.price_change_pct != null ? `${row.price_change_pct >= 0 ? '+' : ''}${row.price_change_pct.toFixed(2)}%` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>}

      {/* Info footer */}
      <div className="mt-8 p-3 bg-gray-900/50 border border-gray-800 rounded-lg text-xs text-gray-500">
        <p className="mb-1">
          Alerts check every 5 minutes. Same signal won't re-alert within 1 hour.
        </p>
        <p>
          "Strong" signals require higher confidence: Technical &ge;65 score, SMC &ge;75% confluence,
          Price Action &ge;65% + volume spike, UAlgo &ge;65% + fresh flip.
        </p>
      </div>
    </div>
  );
};

export default Alerts;
